import React, { type ReactNode } from 'react';
import memoize from 'lodash/memoize';
import { Box, xcss } from '@atlaskit/primitives';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

/**
 * We memoize this event by the **first** argument, e.g. fieldType, as we only really care about the proportion of
 * users encountering this scenario. We don't need to send an event each time this component is rendered.
 */
export const fireTrackAnalyticsOncePerFieldType = memoize(
	(fieldType: string, numberOfElements: number, createAnalyticsEvent) => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'tableCell multipleElementsRendered', {
			fieldType,
			numberOfElements,
		});
	},
);

type Props = {
	/**
	 * Table cell element to render for an issue field.
	 */
	cellElements: ReactNode[];
	/**
	 * Flag to enable min-height containers around each individual cell element as a stop-gap solution to fix layout
	 * issues with duplicate inline editable fields (which have built-in `min-height: 100%`).
	 * TODO JSC-1783 Handle duplicate field layouts within single row height
	 */
	multipleFieldLayoutAdjustment?: boolean;
	/**
	 * Type of the issue field.
	 */
	type: string;
};

export const MultipleCellElements = ({
	cellElements,
	multipleFieldLayoutAdjustment = false,
	type,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	fireTrackAnalyticsOncePerFieldType(type, cellElements.length, createAnalyticsEvent);
	return multipleFieldLayoutAdjustment ? (
		<Box xcss={containerStyles}>
			{cellElements.map((cellElement, index) => (
				<Box key={index} xcss={elementContainerStyles}>
					{cellElement}
				</Box>
			))}
		</Box>
	) : (
		cellElements
	);
};

const containerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	gap: 'space.050',
});

const elementContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	minHeight: '32px',
});
